import React, { useEffect, useRef, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import schultetable from '../../assets/images/The-Schulte-Table.jpg'
import UserSelector from '../../components/UserSelector'
import { useGetGameReportsQuery, useSaveGameReportMutation } from '../../redux/api/activityApi'
import { Timer } from './MemoryGame'
import s from './Schutle.module.css'

const shuffle = (array) => {
  return [...array].sort(() => Math.random() - 0.5)
}

const numbers = Array.from({ length: 25 }, (_, i) => i + 1)
const alphabets = Array.from({ length: 25 }, (_, i) => String.fromCharCode(65 + i))
const alphaNumeric = [
  ...Array.from({ length: 13 }, (_, i) => i + 1),
  ...Array.from({ length: 12 }, (_, i) => String.fromCharCode(65 + i)),
]

const Schutle = () => {
  const [cards, setCards] = useState([])
  const [score, setScore] = useState('')

  const [isPlaying, setIsPlaying] = useState(false)
  const [settings, setSettings] = useState({
    type: 'Normal',
    level: '1',
    no_of_questions: 10,
    curr_question: 0,
  })
  const minRef = useRef()

  const { selectedUser } = useSelector((state) => state.user)
  const { data: reports, isLoading: isLoadingReports, isError, error } = useGetGameReportsQuery(
    { userId: selectedUser?.id, gameTitle: 'Schutle' },
    { skip: !selectedUser }
  )
  const [saveGameReport] = useSaveGameReportMutation()

  const newGame = () => {
    setIsPlaying(true)
    setCards((oldCards) => shuffle(oldCards))
  }

  const stopGame = () => {
    setIsPlaying(false)
    setScore('')
  }

  useEffect(() => {
    if (settings.level === '1') {
      setCards(() => shuffle(numbers))
    } else if (settings.level === '2') {
      setCards(() => shuffle(alphabets))
    } else {
      setCards(() => shuffle(alphaNumeric))
    }
  }, [settings.level])

  const handleChange = (e) => {
    const { name, value } = e.target
    setSettings((old) => ({ ...old, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const [minutes, seconds] = minRef.current.split(':')
    const durationInSeconds = parseInt(minutes) * 60 + parseInt(seconds)
    await saveGameReport({
      user_id: selectedUser?.id,
      username: selectedUser.label,
      bet: settings.level,
      time: durationInSeconds,
      category: 'Schutle',
      score: parseInt(score),
      bet_array: [
        JSON.stringify({
          type: settings.type,
          level: settings.level,
          rate: (parseInt(score) / 25) * 100,
          duration: durationInSeconds,
          avg: score,
        }),
      ],
    })

    setScore('')
  }

  return (
    <Row className='h-100'>
      <Col lg={8} xxl={9} className='p-0'>
        <div
          className='d-flex align-items-center justify-content-between bg-white shadow-sm p-4 mb-3'
          style={{ height: '75px' }}
        >
          <div className='d-flex align-items-center gap-3'>
            <img src={schultetable} alt='mental-maths' height={48} width={48} />
            <h1 className={s.title}>Schulte Table</h1>
          </div>

          <div className='d-flex align-items-center gap-4'>
            <p className='m-0'>User</p>
            <UserSelector />
            <div>
              <label htmlFor='type'>Type</label>
              <select
                id='type'
                name='type'
                value={settings.type}
                onChange={handleChange}
                required
                disabled={isPlaying}
                style={{ width: 'fit-content', padding: 0, height: '25px' }}
                className='ms-2 border border-2 rounded-pill'
              >
                <option value='Normal'>Normal</option>
                <option value='Reverse'>Reverse</option>
              </select>
            </div>
            <div>
              <label htmlFor='level'>Level</label>
              <select
                id='level'
                name='level'
                value={settings.level}
                onChange={handleChange}
                required
                disabled={isPlaying}
                style={{ width: 'fit-content', padding: 0, height: '25px' }}
                className='ms-2 border border-2 rounded-pill'
              >
                {[1, 2, 3].map((lvl) => (
                  <option key={lvl} value={lvl}>
                    {lvl}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className='mx-auto' style={{ width: 'min-content' }}>
          <div
            className={`d-flex align-items-center ${
              isPlaying ? 'justify-content-between' : 'justify-content-end'
            } gap-4 mb-3`}
          >
            {isPlaying && (
              <div className='px-3 py-1 rounded-pill bg-white shadow-sm'>
                <span className='text-secondary me-2'>Time</span>
                <Timer isPlaying={isPlaying} minRef={minRef} />
              </div>
            )}
            {!isPlaying ? (
              <button type='button' onClick={newGame} className='py-2 px-4 border-0 rounded-3 text-black bg-warning'>
                Start
              </button>
            ) : (
              <button type='button' onClick={stopGame} className='py-2 px-4 border-0 rounded-3 bg-black text-white'>
                Stop
              </button>
            )}
          </div>

          <div
            className='Container p-2 shadow rounded-3'
            style={{ display: 'grid', gap: '6px', gridTemplateColumns: `repeat(5, auto)` }}
          >
            {cards.map((value, i) => {
              return (
                <div key={i} className={`${s.Square} ${i === 12 ? s.CorrectSquare : s.FillSquare}`}>
                  {value}
                </div>
              )
            })}
          </div>
          <form className='d-flex gap-2 mt-3' onSubmit={handleSubmit}>
            <input
              required
              type='number'
              min={0}
              max={25}
              placeholder='items counted'
              value={score}
              onChange={(e) => setScore(e.target.value)}
              disabled={isPlaying}
            />
            <button className='py-2 px-4 border-0 rounded-3 bg-black text-white'>Save</button>
          </form>
        </div>
      </Col>

      <Col
        lg={4}
        xxl={3}
        className='bg-white p-4'
        style={{ opacity: isPlaying ? 0 : 1, transition: 'opacity 0.2s ease' }}
      >
        <h6>Game Info</h6>
        <table className='table table-borderless table-sm' style={{ fontSize: 14 }}>
          <tbody>
            <tr>
              <td>Mode</td>
              <td className='fw-bold'>{settings.type}</td>
            </tr>
            <tr>
              <td>Level</td>
              <td className='fw-bold'>{settings.level}</td>
            </tr>
            <tr>
              <td>Number of questions</td>
              <td className='fw-bold'>{settings.no_of_questions}</td>
            </tr>
          </tbody>
        </table>

        <h6>Reports</h6>
        {isLoadingReports ? (
          'Loading'
        ) : isError ? (
          <p>{error.message}</p>
        ) : reports?.length === 0 ? (
          'No reports yet'
        ) : (
          <div style={{ overflowY: 'auto', maxHeight: '200px' }}>
            <table className='table table-sm table-hover' style={{ fontSize: '10px' }}>
              <thead className='bg-white table-group-divider align-middle position-sticky top-0'>
                <tr>
                  <th>#</th>
                  <th>Type</th>
                  <th>Level</th>
                  <th>Rate</th>
                  <th>Counted</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody className='table-group-divider align-middle'>
                {reports.map((report, index) => (
                  <tr key={index}>
                    <td>{index + 1}.</td>
                    <td>{report.type}</td>
                    <td>{report.level}</td>
                    <td>{report.rate}%</td>
                    <td>{report.avg}</td>
                    <td>{report.duration}s</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <h6>How to play</h6>
        <ol style={{ fontSize: 14 }}>
          <li>To start the game press start button.</li>
          <li>Look at the highlighted item in the center and find the item in correct order</li>
          <li>Once done enter the number of item you could find</li>
          <li>Press enter or click to save</li>
        </ol>
      </Col>
    </Row>
  )
}

export default Schutle
